import React, { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMutation } from "../../hooks/useExclusiveMutation";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";

import { artistsApi, eventFinancesApi, eventsApi, ticketTiersApi, venuesApi } from "../../api";
import EventFormFields, { type EventFormValues } from "../../components/events/EventFormFields";
import { QueryErrorBanner } from "../../components/feedback/QueryFeedback";
import { workspaceNav } from "../../routes/workspaceNavigation";
import { useAuth } from "../../context/AuthContext";
import { queryKeys } from "../../queryKeys";
import type { Artist, EventDetail, EventListItem, Venue } from "../../types";
import { artistDisplayName } from "../../utils/artistProfile";
import type { WorkspaceId } from "../../utils/workspaceStorage";
import { venuePrimaryCity } from "../../utils/venueAddress";
import { isPastDateKey } from "../../utils/calendarDates";
import {
  findTierBelowSold,
  findVenueConflict,
  isCancelledStatus,
  isoToDateInput,
  isoToDatetimeLocal,
  normalizeTicketTiers,
  ticketCapacityError,
  tierBelowSoldMessage,
  toIsoOrUndefined,
  venueConflictMessage,
  type TicketTierDraft,
} from "../../utils/eventForm";
import "./EventEditPage.css";

type Props = {
  workspace: WorkspaceId;
};

const emptyForm = (): EventFormValues => ({
  eventName: "",
  selectedArtistId: "",
  eventDate: "",
  startAt: "",
  endAt: "",
  venueId: "",
  eventCapacity: "",
  // FE-3 / BE-8: blank default so the placeholder guides the user.
  ticketTiers: [{ id: crypto.randomUUID(), tier_name: "", price: "", quantity: "" }],
  productionCost: "",
  marketingCost: "",
  artistFee: "",
  status: "draft",
  holdLevel: "",
});

function eventToForm(event: EventDetail): EventFormValues {
  return {
    eventName: event.name,
    selectedArtistId: event.artist_id ?? "",
    eventDate: isoToDateInput(event.event_date),
    startAt: isoToDatetimeLocal(event.start_date) || (event.event_date ? `${isoToDateInput(event.event_date)}T18:00` : ""),
    endAt: isoToDatetimeLocal(event.end_date) || (event.event_date ? `${isoToDateInput(event.event_date)}T23:00` : ""),
    venueId: event.venue_id ?? "",
    eventCapacity: String(event.venue_capacity ?? ""),
    ticketTiers:
      event.ticket_tiers.length > 0
        ? event.ticket_tiers.map((tier) => ({
            id: crypto.randomUUID(),
            serverId: tier.id,
            tier_name: tier.tier_name,
            price: String(tier.price),
            quantity: String(tier.quantity),
            sold: tier.sold,
          }))
        : [{ id: crypto.randomUUID(), tier_name: "", price: "", quantity: "" }],
    productionCost: String(event.finances?.production_cost ?? ""),
    marketingCost: String(event.finances?.marketing_cost ?? ""),
    artistFee: String(event.finances?.artist_fee ?? ""),
    status: event.status,
    holdLevel: event.hold_level ? String(event.hold_level) : "",
  };
}

const EventEditPage: React.FC<Props> = ({ workspace }) => {
  const { eventId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const eventsPath = workspaceNav.events(workspace);

  const [form, setForm] = useState<EventFormValues>(emptyForm);
  const [removedTierIds, setRemovedTierIds] = useState<string[]>([]);
  const [hydrated, setHydrated] = useState(false);

  const artistsQuery = useQuery({
    queryKey: token ? queryKeys.artists.list(workspace) : queryKeys.artists.all(workspace),
    queryFn: () => artistsApi.list(token!),
    enabled: Boolean(token),
  });

  const venuesQuery = useQuery({
    queryKey: token ? queryKeys.venues.list(workspace) : queryKeys.venues.all(workspace),
    queryFn: () => venuesApi.list(token!),
    enabled: Boolean(token),
  });

  const eventQuery = useQuery({
    queryKey: eventId && token ? queryKeys.events.detail(workspace, eventId) : queryKeys.events.all(workspace),
    queryFn: () => eventsApi.detail(eventId!, token!),
    enabled: Boolean(token && eventId),
  });

  useEffect(() => {
    if (!eventQuery.data || hydrated) return;
    setForm(eventToForm(eventQuery.data));
    setRemovedTierIds([]);
    setHydrated(true);
  }, [eventQuery.data, hydrated]);

  useEffect(() => {
    setHydrated(false);
    setForm(emptyForm());
    setRemovedTierIds([]);
  }, [eventId]);

  useEffect(() => {
    if (!form.eventDate) return;
    if (!form.startAt) setForm((prev) => ({ ...prev, startAt: `${form.eventDate}T18:00` }));
    if (!form.endAt) setForm((prev) => ({ ...prev, endAt: `${form.eventDate}T23:00` }));
  }, [form.eventDate, form.endAt, form.startAt]);

  const artists = useMemo<Artist[]>(() => artistsQuery.data ?? [], [artistsQuery.data]);
  const venues = useMemo<Venue[]>(() => venuesQuery.data ?? [], [venuesQuery.data]);
  const selectedArtistName = useMemo(
    () => {
      const artist = artists.find((item) => item.id === form.selectedArtistId);
      return artist ? artistDisplayName(artist) : null;
    },
    [artists, form.selectedArtistId],
  );

  const patchForm = (patch: Partial<EventFormValues>) => setForm((prev) => ({ ...prev, ...patch }));

  const patchTier = (id: string, patch: Partial<TicketTierDraft>) => {
    setForm((prev) => ({
      ...prev,
      ticketTiers: prev.ticketTiers.map((tier) => (tier.id === id ? { ...tier, ...patch } : tier)),
    }));
  };

  const addTier = () => {
    setForm((prev) => ({
      ...prev,
      ticketTiers: [...prev.ticketTiers, { id: crypto.randomUUID(), tier_name: "", price: "", quantity: "" }],
    }));
  };

  const removeTier = (id: string) => {
    setForm((prev) => {
      if (prev.ticketTiers.length <= 1) return prev;
      const target = prev.ticketTiers.find((tier) => tier.id === id);
      if (target?.serverId) {
        setRemovedTierIds((ids) => [...ids, target.serverId!]);
      }
      return { ...prev, ticketTiers: prev.ticketTiers.filter((tier) => tier.id !== id) };
    });
  };

  const invalidateCaches = async () => {
    if (!eventId) return;
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.events.all(workspace) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.events.detail(workspace, eventId) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.events.dashboard(workspace, eventId) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.eventOs.all(workspace) }),
    ]);
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!token || !eventId) throw new Error("Session expired. Sign in again.");

      const normalizedTiers = normalizeTicketTiers(form.ticketTiers);
      if (!normalizedTiers) throw new Error("Add at least one valid ticket tier.");

      if (!form.venueId) throw new Error("Select a venue — event location is taken from the venue address.");

      const venue = venues.find((item) => item.id === form.venueId);
      const city = venue ? venuePrimaryCity(venue) : "";
      if (!city) throw new Error("Selected venue has no city on file. Update the venue address in Venues registry.");

      const originalDate = eventQuery.data ? isoToDateInput(eventQuery.data.event_date) : "";
      if (form.eventDate !== originalDate && isPastDateKey(form.eventDate)) {
        throw new Error("Event date cannot be moved to the past.");
      }

      const belowSold = findTierBelowSold(form.ticketTiers);
      if (belowSold) throw new Error(tierBelowSoldMessage(belowSold.tier_name, belowSold.sold ?? 0));

      const capacityError = ticketCapacityError(form.ticketTiers, form.eventCapacity);
      if (capacityError) throw new Error(capacityError);

      // Only re-check when the slot actually changed, so a pre-existing overlap
      // doesn't block unrelated edits (or cancelling the event).
      const original = eventQuery.data ? eventToForm(eventQuery.data) : null;
      const slotChanged =
        !original ||
        form.venueId !== original.venueId ||
        form.eventDate !== original.eventDate ||
        form.startAt !== original.startAt ||
        form.endAt !== original.endAt ||
        isCancelledStatus(original.status);
      if (slotChanged && !isCancelledStatus(form.status)) {
        let existingEvents: EventListItem[];
        try {
          existingEvents = await queryClient.fetchQuery({
            queryKey: queryKeys.events.list(workspace),
            queryFn: () => eventsApi.list(token),
            staleTime: 0,
          });
        } catch {
          throw new Error("Could not check venue availability. Try again.");
        }
        const conflict = findVenueConflict(existingEvents, {
          venueId: form.venueId,
          eventDate: form.eventDate,
          startAt: form.startAt,
          endAt: form.endAt,
          excludeEventId: eventId,
        });
        if (conflict) throw new Error(venueConflictMessage(conflict));
      }

      const prod = Number(form.productionCost);
      const mkt = Number(form.marketingCost);
      const fee = Number(form.artistFee);
      if (!Number.isFinite(prod) || prod < 0 || !Number.isFinite(mkt) || mkt < 0 || !Number.isFinite(fee) || fee < 0) {
        throw new Error("Enter non-negative numbers for all costs.");
      }

      await eventsApi.update(
        eventId,
        {
          name: form.eventName.trim(),
          artist: selectedArtistName,
          artist_id: form.selectedArtistId || null,
          city,
          venue_capacity: Number(form.eventCapacity),
          event_date: form.eventDate,
          start_date: toIsoOrUndefined(form.startAt) ?? toIsoOrUndefined(`${form.eventDate}T18:00`),
          end_date: toIsoOrUndefined(form.endAt) ?? toIsoOrUndefined(`${form.eventDate}T23:00`),
          venue_id: form.venueId,
          status: form.status,
          hold_level: form.holdLevel ? Number(form.holdLevel) : null,
        },
        token,
      );

      await eventFinancesApi.update(
        eventId,
        { production_cost: prod, marketing_cost: mkt, artist_fee: fee },
        token,
      );

      const tierOps: Promise<unknown>[] = [];

      for (const removedId of removedTierIds) {
        tierOps.push(ticketTiersApi.remove(eventId, removedId, token));
      }

      for (const tier of form.ticketTiers) {
        const tier_name = tier.tier_name.trim();
        const price = Number(tier.price);
        const quantity = Number(tier.quantity);
        if (!tier_name || !Number.isFinite(price) || !Number.isFinite(quantity) || quantity <= 0) continue;

        if (tier.serverId) {
          tierOps.push(
            ticketTiersApi.update(eventId, tier.serverId, { tier_name, price, quantity }, token),
          );
        } else {
          tierOps.push(ticketTiersApi.create(eventId, { tier_name, price, quantity }, token));
        }
      }

      await Promise.all(tierOps);
    },
    onSuccess: async () => {
      toast.success("Event updated");
      await invalidateCaches();
      if (eventId) {
        navigate(workspaceNav.dashboard(workspace, eventId), { replace: true });
      }
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to save event");
    },
  });

  if (!eventId) {
    return <Navigate to={eventsPath} replace />;
  }

  const loadingEvent = eventQuery.isPending;
  const errorMsg =
    eventQuery.error instanceof Error ? eventQuery.error.message : eventQuery.error ? String(eventQuery.error) : "";

  return (
    <div className="event-edit-page">
      <Link to={workspaceNav.dashboard(workspace, eventId)} className="app-shell-back-link">
        ← Back to dashboard
      </Link>

      <div className="eventos-onboarding-heading-row">
        <h1 className="app-shell-page-title event-edit-page__title">Edit event</h1>
        <p className="app-shell-lede event-edit-page__lede">
          Update show details, ticket tiers, and cost assumptions. Changes sync to your dashboard and command center.
        </p>
      </div>

      {eventQuery.isError ? (
        <QueryErrorBanner
          title="Could not load event"
          message={errorMsg || "Unknown error"}
          onRetry={() => void eventQuery.refetch()}
          retrying={eventQuery.isFetching}
        />
      ) : null}

      <div className="eventos-card eventos-form-card-scroll event-edit-page__card">
        <div className="event-edit-page__card-head">
          <div className="eventos-label">Event settings</div>
          {eventQuery.data ? (
            <span className="app-shell-pill event-edit-page__status-pill">{eventQuery.data.status.replace(/_/g, " ")}</span>
          ) : null}
        </div>

        {loadingEvent ? (
          <p className="eventos-subtle event-edit-page__loading">Loading event details…</p>
        ) : eventQuery.data ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              saveMutation.mutate();
            }}
            className="eventos-form event-edit-page__form"
          >
            <EventFormFields
              mode="edit"
              artists={artists}
              venues={venues}
              values={form}
              originalEventDate={isoToDateInput(eventQuery.data.event_date)}
              onPatch={patchForm}
              onPatchTier={patchTier}
              onAddTier={addTier}
              onRemoveTier={removeTier}
            />

            <div className="app-form-actions event-edit-page__actions">
              <button className="eventos-primary-btn" type="submit" disabled={saveMutation.isPending}>
                {saveMutation.isPending ? "Saving…" : "Save changes"}
              </button>
              <Link to={workspaceNav.dashboard(workspace, eventId)} className="eventos-nav-btn event-edit-page__cancel">
                Cancel
              </Link>
            </div>
          </form>
        ) : null}
      </div>
    </div>
  );
};

export default EventEditPage;
